import API from "@/axios/API";
import CommonTable from "@/components/Common/Table";
import PageHeader from "@/components/Common/UI/PageHeader";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import AutoAwesomeMotionRoundedIcon from "@mui/icons-material/AutoAwesomeMotionRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocalFireDepartmentRoundedIcon from "@mui/icons-material/LocalFireDepartmentRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import SmartphoneRoundedIcon from "@mui/icons-material/SmartphoneRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { tokens } from "@/theme/tokens";

const THEME = {
    primary: tokens.color.primary,
    primary08: tokens.color.primary08,
    primary16: tokens.color.primary16,
    bgDefault: tokens.color.bgDefault,
    bgPaper: tokens.color.bgPaper,
    bgLight: tokens.color.bgLight,
    bgTableHeader: tokens.color.bgTableHeader,
    textPrimary: tokens.color.textPrimary,
    textSecondary: tokens.color.textSecondary,
    textDark: tokens.color.textDark,
    textMedium: tokens.color.textMedium,
    divider: tokens.color.divider,
    border: tokens.color.border,
    warning: tokens.color.warning,
    warningBg: tokens.color.warningBg,
    warningText: tokens.color.warningText,
    error: tokens.color.error,
    errorBg: tokens.color.errorBg,
    success: tokens.color.success,
    successBg: tokens.color.successBg,
    info: tokens.color.info,
    infoBg: tokens.color.infoBg,
    radius: tokens.radius,
    shadow: tokens.shadow,
    font: tokens.font.family,
    mono: tokens.font.monospace,
};

/* ------------------------------------------------------------------ */
/*  HELPERS — flatten MongoDB extended-JSON                            */
/* ------------------------------------------------------------------ */
const oid = (v) => (v && typeof v === "object" && "$oid" in v ? v.$oid : String(v ?? ""));
const long = (v) => (v && typeof v === "object" && "$numberLong" in v ? v.$numberLong : String(v ?? ""));
const date = (v) => (v && typeof v === "object" && "$date" in v ? v.$date : v);
const fmtTime = (v) => {
    const d = new Date(date(v));
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });
};
const shortDevice = (ua = "") => {
    if (!ua) return "Unknown";
    if (/iPhone|iPad|iOS/i.test(ua)) return "iOS";
    if (/Android/i.test(ua)) return "Android";
    if (/Windows/i.test(ua)) return "Windows";
    if (/Mac OS X|Macintosh/i.test(ua)) return "macOS";
    if (/Linux/i.test(ua)) return "Linux";
    return "Web";
};

/* ------------------------------------------------------------------ */
/*  MODULE + STATUS META                                               */
/* ------------------------------------------------------------------ */
const MODULE_META = {
    AUTHENTICATION: {
        label: "Authentication",
        Icon: LoginRoundedIcon,
        bg: "#E3F2FD",
        fg: "#1565C0",
    },
    ORDERS: {
        label: "Orders",
        Icon: ShoppingCartRoundedIcon,
        bg: "#E8F5E9",
        fg: "#2E7D32",
    },
    BILLING: {
        label: "Billing",
        Icon: ReceiptLongRoundedIcon,
        bg: THEME.warningBg,
        fg: "#F57F17",
    },
    NOTIFICATIONS: {
        label: "Notifications",
        Icon: NotificationsRoundedIcon,
        bg: "#E8EAF6",
        fg: "#3949AB",
    },
    SUPPORT: {
        label: "Support",
        Icon: SupportAgentRoundedIcon,
        bg: "#F3E5F5",
        fg: "#7B1FA2",
    },
    USER_MANAGEMENT: {
        label: "User Management",
        Icon: GroupRoundedIcon,
        bg: "#E8F5E9",
        fg: "#388E3C",
    },
    PAYMENT_GATEWAY: {
        label: "Payment Gateway",
        Icon: CreditCardRoundedIcon,
        bg: "#FFF8E1",
        fg: "#FF8F00",
    },
    WORKSPACE_PROVISIONING: {
        label: "Workspace Provisioning",
        Icon: BusinessRoundedIcon,
        bg: "#E0F7FA",
        fg: "#00838F",
    },
    SEQUENCER: {
        label: "Sequencer",
        Icon: AutoAwesomeMotionRoundedIcon,
        bg: "#EDE7F6",
        fg: "#5E35B1",
    },
    PRE_WARMUP: {
        label: "Pre Warmup",
        Icon: LocalFireDepartmentRoundedIcon,
        bg: "#FBE9E7",
        fg: "#D84315",
    },
    WALLET: {
        label: "Wallet",
        Icon: AccountBalanceWalletRoundedIcon,
        bg: "#E0F2F1",
        fg: "#00695C",
    },
    DOMAIN: {
        label: "Domain",
        Icon: PublicRoundedIcon,
        bg: "#E1F5FE",
        fg: "#0277BD",
    },
};

const STATUS_META = {
    SUCCESS: { label: "Success", bg: THEME.successBg, fg: THEME.success, Icon: CheckCircleRoundedIcon },
    FAILURE: { label: "Failure", bg: "#FFEBEE", fg: "#C62828", Icon: CancelRoundedIcon },
    PENDING: { label: "Pending", bg: "#FFF3E0", fg: "#E65100", Icon: HourglassEmptyRoundedIcon },
};

/* ------------------------------------------------------------------ */
/*  SMALL BUILDING BLOCKS                                              */
/* ------------------------------------------------------------------ */
const Pill = ({ bg, fg, icon, children }) => (
    <Chip
        size="small"
        icon={icon}
        label={children}
        sx={{
            height: 24,
            borderRadius: THEME.radius.pill / 2,
            bgcolor: bg,
            color: fg,
            fontWeight: 600,
            fontSize: 11.5,
            letterSpacing: 0.1,
            "& .MuiChip-icon": { color: fg, ml: 0.75, fontSize: 14 },
            "& .MuiChip-label": { px: 1 },
        }}
    />
);

const ModuleChip = ({ m }) => {
    const c = MODULE_META[m] || { label: m, Icon: InfoOutlinedIcon, bg: "#eee", fg: "#555" };
    const I = c.Icon;
    return <Pill bg={c.bg} fg={c.fg} icon={<I sx={{ fontSize: 14 }} />}>{c.label}</Pill>;
};

const StatusChip = ({ s }) => {
    const c = STATUS_META[s] || { label: s, bg: "#eee", fg: "#555", Icon: InfoOutlinedIcon };
    const I = c.Icon;
    return <Pill bg={c.bg} fg={c.fg} icon={<I sx={{ fontSize: 14 }} />}>{c.label}</Pill>;
};

/* ------------------------------------------------------------------ */
/*  DETAIL DIALOG                                                      */
/* ------------------------------------------------------------------ */
const KV = ({ label, value, mono }) => (
    <Box sx={{ minWidth: 0 }}>
        <Typography
            sx={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                color: THEME.textSecondary,
            }}
        >
            {label}
        </Typography>
        <Typography
            sx={{
                mt: 0.25,
                fontSize: 13,
                color: THEME.textPrimary,
                fontFamily: mono ? THEME.mono : "inherit",
                wordBreak: "break-all",
            }}
        >
            {value ?? "—"}
        </Typography>
    </Box>
);

const DetailModal = ({ open, onClose, log }) => {
    if (!log) return null;
    const st = STATUS_META[log.status] || STATUS_META.PENDING;
    const StIcon = st.Icon;
    const metaEntries = Object.entries(log.metadata || {});

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: `${THEME.radius.dialog}px`,
                    overflow: "hidden",
                    border: `1px solid ${THEME.divider}`,
                },
            }}
        >
            <DialogTitle sx={{ p: 0 }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        px: 3,
                        py: 2,
                        bgcolor: THEME.bgLight,
                        borderBottom: `1px solid ${THEME.divider}`,
                    }}
                >
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            bgcolor: st.bg,
                            color: st.fg,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <StIcon sx={{ fontSize: 22 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 15, fontWeight: 700, color: THEME.textDark }}>
                            Event Details
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: THEME.textSecondary, mt: 0.25 }} noWrap>
                            {log.eventType} — {log.eventId}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={onClose}>
                        <CloseRoundedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1, mb: 2.5, pt: 1 }}>
                    <ModuleChip m={log.module} />
                    <StatusChip s={log.status} />
                    {log.logLevel && (
                        <Pill bg={THEME.primary08} fg={THEME.primary} icon={<InfoOutlinedIcon sx={{ fontSize: 14 }} />}>
                            {log.logLevel}
                        </Pill>
                    )}
                </Stack>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 2,
                    }}
                >
                    <KV label="Action" value={log.action} />
                    <KV label="Timestamp" value={fmtTime(log.timestamp)} />
                    <KV label="User" value={log.userEmail || `#${long(log.userId)}`} />
                    <KV label="Role" value={log.userRole || "—"} />
                    <KV label="IP Address" value={log.ipAddress || "—"} mono />
                    <KV label="Device" value={shortDevice(log.device)} />
                    <KV label="Request ID" value={log.requestId || "—"} mono />
                    <KV label="Entity ID" value={log.entityId || "—"} mono />
                </Box>

                {log.errorMessage && (
                    <Box
                        sx={{
                            mt: 2.5,
                            p: 1.5,
                            borderRadius: `${THEME.radius.card}px`,
                            bgcolor: "#FFEBEE",
                            border: "1px solid #FFCDD2",
                        }}
                    >
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                            <ErrorOutlineRoundedIcon sx={{ fontSize: 16, color: "#C62828" }} />
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#C62828" }}>Error</Typography>
                        </Stack>
                        <Typography sx={{ fontSize: 12.5, color: "#B71C1C" }}>{log.errorMessage}</Typography>
                    </Box>
                )}

                {metaEntries.length > 0 && (
                    <Box sx={{ mt: 2.5 }}>
                        <Typography
                            sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: 0.6,
                                color: THEME.textSecondary,
                                mb: 1,
                            }}
                        >
                            Metadata
                        </Typography>
                        <Box
                            sx={{
                                borderRadius: `${THEME.radius.card}px`,
                                border: `1px solid ${THEME.divider}`,
                                bgcolor: THEME.bgLight,
                                overflow: "hidden",
                            }}
                        >
                            {metaEntries.map(([k, v], i) => (
                                <Box
                                    key={k}
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: { xs: "1fr", sm: "160px 1fr" },
                                        gap: 1.5,
                                        px: 1.5,
                                        py: 1,
                                        borderTop: i === 0 ? "none" : `1px solid ${THEME.divider}`,
                                    }}
                                >
                                    <Typography sx={{ fontSize: 12.5, color: THEME.textSecondary, fontWeight: 600 }}>
                                        {k}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 12.5,
                                            fontFamily: THEME.mono,
                                            color: THEME.textPrimary,
                                            wordBreak: "break-all",
                                        }}
                                    >
                                        {typeof v === "object" && v !== null
                                            ? "$date" in v
                                                ? fmtTime(v)
                                                : "$numberLong" in v
                                                    ? long(v)
                                                    : "$oid" in v
                                                        ? oid(v)
                                                        : JSON.stringify(v)
                                            : String(v)}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                <Typography sx={{ mt: 2, fontSize: 10.5, fontFamily: THEME.mono, color: THEME.textSecondary }}>
                    _id: {oid(log._id)}
                </Typography>
            </DialogContent>

            <Divider />
            <Box sx={{ display: "flex", justifyContent: "flex-end", px: 3, py: 1.5, bgcolor: THEME.bgLight }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        textTransform: "none",
                        borderRadius: `${THEME.radius.base}px`,
                        borderColor: THEME.border,
                        color: THEME.textPrimary,
                        "&:hover": { borderColor: THEME.primary, color: THEME.primary, bgcolor: THEME.primary08 },
                    }}
                >
                    Close
                </Button>
            </Box>
        </Dialog>
    );
};

/* ------------------------------------------------------------------ */
/*  COLUMN DEFINITIONS                                                 */
/* ------------------------------------------------------------------ */
const PAGE_SIZE = 20;

const AUDIT_BASE = `${import.meta.env.VITE_BACKEND_API || import.meta.env.VITE_BACKEND_API || ""}/auth/admin/audit-logs`;

const buildColumns = (setSelected) => [
    {
        key: "id",
        label: "Event #",
        render: (log) => {
            const idShort = (oid(log._id) || log.eventId || "").slice(-6);
            return (
                <Button
                    onClick={() => setSelected(log)}
                    sx={{
                        p: 0,
                        minWidth: 0,
                        textTransform: "none",
                        fontFamily: THEME.mono,
                        fontWeight: 600,
                        fontSize: 12,
                        color: THEME.primary,
                        "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
                    }}
                >
                    #{idShort}
                </Button>
            );
        },
    },
    {
        key: "module",
        label: "Module",
        filterable: true,
        filterConfig: {
            heading: "Filter by Module",
            chips: Object.entries(MODULE_META).map(([value, m]) => ({
                value,
                label: m.label,
                dot: m.fg,
            })),
        },
        render: (log) => <ModuleChip m={log.module} />,
    },
    {
        key: "eventType",
        label: "Event Type",
        filterable: true,
        filterConfig: {
            heading: "Filter by Event Type",
            text: true,
            textPlaceholder: "e.g. LOGIN_SUCCESS",
        },
        cellSx: { fontSize: 12.5, fontWeight: 600, color: THEME.textPrimary, whiteSpace: "nowrap" },
        render: (log) => log.eventType,
    },
    {
        key: "action",
        label: "Action",
        filterable: true,
        filterConfig: {
            heading: "Filter by Action",
            text: true,
            textPlaceholder: "e.g. LOGIN, CREATE",
        },
        cellSx: { fontSize: 12.5, color: THEME.textSecondary, whiteSpace: "nowrap" },
        render: (log) => log.action,
    },
    {
        key: "status",
        label: "Status",
        filterable: true,
        filterConfig: {
            heading: "Filter by Status",
            chipsRadio: true,
            chips: Object.entries(STATUS_META).map(([value, s]) => ({
                value,
                label: s.label,
                dot: s.fg,
            })),
        },
        render: (log) => <StatusChip s={log.status} />,
    },
    {
        key: "userEmail",
        label: "User",
        filterable: true,
        filterConfig: {
            heading: "Filter by User",
            text: true,
            textPlaceholder: "Email or user id…",
        },
        render: (log) => (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                <PersonRoundedIcon sx={{ fontSize: 14, color: THEME.textSecondary, flexShrink: 0 }} />
                <Typography
                    sx={{
                        fontSize: 12.5,
                        color: THEME.textPrimary,
                        maxWidth: 180,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {log.userEmail || (log.userId ? `#${long(log.userId)}` : "—")}
                </Typography>
            </Stack>
        ),
    },
    {
        key: "ipAddress",
        label: "IP / Device",
        filterable: true,
        filterConfig: {
            heading: "Filter by IP",
            text: true,
            textPlaceholder: "e.g. 192.168.1.24",
        },
        render: (log) => (
            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, fontFamily: THEME.mono, color: THEME.textPrimary }}>
                    {log.ipAddress || "—"}
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <SmartphoneRoundedIcon sx={{ fontSize: 11, color: THEME.textSecondary }} />
                    <Typography sx={{ fontSize: 10.5, color: THEME.textSecondary }}>
                        {shortDevice(log.device)}
                    </Typography>
                </Stack>
            </Stack>
        ),
    },
    {
        key: "timestamp",
        label: "Timestamp",
        filterable: true,
        filterConfig: {
            heading: "Sort by Timestamp",
            sortOptions: [
                { dir: "desc", label: "Newest first" },
                { dir: "asc", label: "Oldest first" },
            ],
        },
        cellSx: { fontSize: 12, color: THEME.textSecondary, whiteSpace: "nowrap" },
        render: (log) => fmtTime(log.timestamp),
    },
    {
        key: "actions",
        label: "Actions",
        render: (log) => (
            <Tooltip title="View details" arrow>
                <Button
                    onClick={() => setSelected(log)}
                    size="small"
                    variant="outlined"
                    startIcon={<VisibilityRoundedIcon sx={{ fontSize: 14 }} />}
                    sx={{
                        textTransform: "none",
                        fontSize: 12,
                        fontWeight: 600,
                        height: 28,
                        borderRadius: `${THEME.radius.base}px`,
                        borderColor: THEME.border,
                        color: THEME.textPrimary,
                        "&:hover": {
                            borderColor: THEME.primary,
                            color: THEME.primary,
                            bgcolor: THEME.primary08,
                        },
                    }}
                >
                    View
                </Button>
            </Tooltip>
        ),
    },
];

/* ------------------------------------------------------------------ */
/*  MAIN PAGE                                                          */
/* ------------------------------------------------------------------ */
export default function AuditLogs() {

    /* ── state ── */
    const [logs, setLogs] = useState([]);
    const [totalElements, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [query, setQuery] = useState("");
    const [filters, setFilters] = useState({}); // { colKey: { text, checked, chips } }
    const [sort, setSort] = useState({ key: "timestamp", dir: "desc" });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(null);

    /* ── fetch ── */
    const fetchLogs = useCallback(async (pageNum, searchVal, filtersVal, sortVal) => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            params.set("page", pageNum);
            params.set("size", PAGE_SIZE);
            if (searchVal) params.set("entityId", searchVal);

            /* serialize per-column filters */
            Object.entries(filtersVal || {}).forEach(([key, v]) => {
                if (!v) return;
                if (v.text) params.set(key, v.text);
                (v.chips || []).forEach((c) => params.append(key, c));
                (v.checked || []).forEach((c) => params.append(key, c));
            });

            /* sort */
            if (sortVal?.key) params.set("sort", `${sortVal.key},${sortVal.dir}`);

            const res = await API.get(`${AUDIT_BASE}?${params.toString()}`);
            const data = res.data;
            setLogs(data.logs ?? []);
            setTotal(data.totalElements ?? 0);
        } catch (err) {
            setError(err.message || "Failed to load audit logs.");
        } finally {
            setLoading(false);
        }
    }, []);

    /* re-fetch on page / filters / sort change */
    useEffect(() => {
        fetchLogs(page, query, filters, sort);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filters, sort]);


    const handleClearAllFilters = () => {
        setFilters({});
        setSort({ key: "timestamp", dir: "desc" });
        setQuery("");
        setPage(0);
        fetchLogs(0, "", {}, { key: "timestamp", dir: "desc" });
    };

    const handlePageChange = (_, newPage) => setPage(newPage);

    /* ── filter / sort handlers ── */
    const handleApplyFilter = useCallback((colKey, val) => {
        setFilters((prev) => ({ ...prev, [colKey]: val }));
        setPage(0);
    }, []);

    const handleClearFilter = useCallback((colKey) => {
        setFilters((prev) => {
            const next = { ...prev };
            delete next[colKey];
            return next;
        });
        setPage(0);
    }, []);

    const handleSort = useCallback((colKey, dir) => {
        setSort({ key: colKey, dir });
        setPage(0);
    }, []);

    /* ── derived: how many things are actively filtering? ── */
    const activeFilterCount = useMemo(() => {
        let count = 0;
        // per-column filters
        Object.values(filters).forEach((v) => {
            if (!v) return;
            if (v.text) count++;
            count += (v.chips || []).length;
            count += (v.checked || []).length;
        });
        // global search
        if (query.trim()) count++;
        // non-default sort (optional — remove if you don't want sort to count)
        if (sort.key !== "timestamp" || sort.dir !== "desc") count++;
        return count;
    }, [filters, query, sort]);

    const columns = useMemo(() => buildColumns(setSelected), []);


    const emptyState = (
        <Box sx={{ py: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            <DescriptionRoundedIcon sx={{ fontSize: 36, color: THEME.textSecondary, opacity: 0.4 }} />
            <Typography sx={{ fontSize: 13, color: THEME.textSecondary }}>
                No audit events found.
            </Typography>
        </Box>
    );

    /* ── toolbar: search + clear all ── */
    const clearAllFilterButton = (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Button
                size="small"
                onClick={handleClearAllFilters}
                startIcon={<CloseRoundedIcon sx={{ fontSize: 16 }} />}
                sx={{
                    textTransform: "none",
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: THEME.primary,
                    border: `1px solid ${THEME.primaryBorder}`,
                    borderRadius: "8px",
                    px: 1.5,
                    py: 0.5,
                    bgcolor: THEME.primary08,
                    "&:hover": { bgcolor: "rgba(74,102,241,0.14)" },
                }}
            >
                Clear all
                <Box
                    component="span"
                    sx={{
                        ml: 0.75,
                        px: 0.6,
                        py: 0.1,
                        minWidth: 18,
                        height: 18,
                        borderRadius: "999px",
                        bgcolor: THEME.primary,
                        color: "#fff",
                        fontSize: 10,
                        fontWeight: 700,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        lineHeight: 1,
                    }}
                >
                    {activeFilterCount}
                </Box>
            </Button>

        </Box>
    );

    return (
        <Box sx={{ bgcolor: THEME.bgDefault, minHeight: "100%", fontFamily: THEME.font }}>
            {/* Header */}
            <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                spacing={2}
                sx={{ mb: 2.5 }}
            >
                <PageHeader
                    title="Audit Logs"
                    subtitle="Monitor and inspect every platform event — filter by module, status, or user"
                />

            </Stack>

            {/* Error banner */}
            {error && (
                <Box
                    sx={{
                        mb: 2,
                        px: 2,
                        py: 1.25,
                        borderRadius: `${THEME.radius.card}px`,
                        bgcolor: "#FFEBEE",
                        border: "1px solid #FFCDD2",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <ErrorOutlineRoundedIcon sx={{ fontSize: 18, color: "#C62828", flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 13, color: "#B71C1C", flex: 1 }}>{error}</Typography>
                    <IconButton size="small" onClick={() => fetchLogs(page, query, filters, sort)} sx={{ ml: 1 }}>
                        <CloseRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
            )}

            {/* CommonTable */}
            <CommonTable
                columns={columns}
                data={logs}
                rowKey="eventId"
                loading={loading}
                skeletonRows={PAGE_SIZE}
                toolbarRight={clearAllFilterButton}
                emptyState={emptyState}
                totalCount={totalElements}
                page={page}
                rowsPerPage={PAGE_SIZE}
                onPageChange={handlePageChange}
                onApplyFilter={handleApplyFilter}
                onClearFilter={handleClearFilter}
                onSort={handleSort}
            />

            <DetailModal open={!!selected} onClose={() => setSelected(null)} log={selected} />
        </Box>
    );
}
